/**
 * Las dos casillas de aceptación y el botón para continuar.
 *
 * Dos casillas y no una (diseño aprobado el 25-sep-2026): la autorización
 * de datos personales tiene que ser expresa e identificable (Ley 1581,
 * art. 9), y mezclarla con los términos la debilita como prueba. El botón
 * queda deshabilitado hasta marcar ambas y el texto de ayuda dice qué falta.
 *
 * Lo usan el último paso del onboarding y la pantalla de actualización.
 * `onSubmit` recibe las versiones del texto que la persona tuvo en pantalla.
 */
import { useState, type ReactNode } from "react";

import { LegalViewer } from "@/components/legal/LegalViewer";
import { LEGAL_DOCUMENTS, type LegalDocId } from "@/legal/documentos";

const CHECK_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" className="h-3 w-3">
    <path d="M5 12l5 5 9-10" />
  </svg>
);

export function LegalConsent({
  submitLabel,
  pendingLabel,
  submitTestId,
  isPending,
  error,
  onSubmit,
}: {
  submitLabel: string;
  pendingLabel: string;
  submitTestId: string;
  isPending: boolean;
  error: string | null;
  onSubmit: (versions: Record<LegalDocId, string>) => void;
}) {
  const [accepted, setAccepted] = useState<Record<LegalDocId, boolean>>({
    terminos: false,
    privacidad: false,
  });
  const [viewer, setViewer] = useState<LegalDocId | null>(null);
  const bothAccepted = accepted.terminos && accepted.privacidad;
  const terminos = LEGAL_DOCUMENTS.terminos;
  const privacidad = LEGAL_DOCUMENTS.privacidad;

  const docLink = (id: LegalDocId, label: string) => (
    <button
      type="button"
      onClick={(e) => {
        // El enlace vive dentro del <label>: sin esto, abrirlo marcaría la casilla.
        e.preventDefault();
        setViewer(id);
      }}
      data-testid={`legal-open-${id}`}
      className="font-semibold text-brand-700 hover:underline"
    >
      {label}
    </button>
  );

  const box = (id: LegalDocId, text: ReactNode, detail: string) => {
    const on = accepted[id];
    return (
      <label
        htmlFor={`legal-check-${id}`}
        className={
          "flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 transition-colors " +
          (on ? "border-cta-500 bg-cta-50" : "border-slate-200 bg-white hover:border-slate-300")
        }
      >
        <span className="relative mt-0.5 flex h-5 w-5 shrink-0">
          <input
            id={`legal-check-${id}`}
            type="checkbox"
            checked={on}
            onChange={(e) => setAccepted((prev) => ({ ...prev, [id]: e.target.checked }))}
            data-testid={`legal-check-${id}`}
            className="peer absolute inset-0 m-0 h-5 w-5 cursor-pointer opacity-0"
          />
          <span
            aria-hidden="true"
            className={
              "flex h-5 w-5 items-center justify-center rounded-md border-2 text-white peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 " +
              (on ? "border-cta-500 bg-cta-500" : "border-slate-400 bg-white")
            }
          >
            {on && CHECK_ICON}
          </span>
        </span>
        <span className="text-sm text-slate-900">
          {text}
          <span className="mt-0.5 block text-xs text-slate-500">{detail}</span>
        </span>
      </label>
    );
  };

  return (
    <div>
      <div className="flex flex-col gap-2.5">
        {box(
          "terminos",
          <>He leído y acepto los {docLink("terminos", terminos.shortTitle)} de Kaptar.</>,
          `Versión ${terminos.version} · vigente desde el ${terminos.vigenteDesde}`,
        )}
        {box(
          "privacidad",
          <>
            Autorizo a Kaptar a tratar mis datos personales según la{" "}
            {docLink("privacidad", "Política de tratamiento de datos")}.
          </>,
          "Ley 1581 de 2012 · puedes revocarla cuando quieras",
        )}
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <button
        type="button"
        data-testid={submitTestId}
        disabled={!bothAccepted || isPending}
        aria-describedby={bothAccepted ? undefined : "legal-consent-help"}
        onClick={() => onSubmit({ terminos: terminos.version, privacidad: privacidad.version })}
        className="mt-5 w-full rounded-md bg-cta-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-cta-600 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-cta-500"
      >
        {isPending ? pendingLabel : submitLabel}
      </button>
      {!bothAccepted && (
        <p id="legal-consent-help" className="mt-2 text-center text-xs text-slate-500">
          Marca las dos casillas para continuar.
        </p>
      )}

      {viewer && <LegalViewer initialDoc={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
